<template>
	<div class="CustomInput materialElement"
		 :class="{ 'has-validationError': !!validationError, 'has-focus':
		 hasFocus, 'has-value': !!value, 'has-placeholder': !!placeholder,
		 'is-textarea': type === 'textarea' }">
		<label class="label" v-if="label">{{ this.label }} <span
			v-if="optional !== undefined">{{ lang.common.optional }}</span></label>
		<textarea v-if="type == 'textarea'" ref="inputElement"
				  @keydown="e => $emit('keydown', e)" @input="oninput"
				  @change="onchange" :type="type" v-model.lazy="value"
				  :placeholder="placeholder" @focus="hasFocus = true"
				  @blur="onblur" />
		<input v-else ref="inputElement" @keydown="e => $emit('keydown', e)"
			   @input="oninput" @change="onchange"
			   :type="overriddenType || type" v-model.lazy="value"
			   :placeholder="placeholder" @focus="hasFocus = true"
			   @blur="onblur" />
		<div class="viewPasswordButton" v-if="type == 'password'"
			 @click="togglePasswordVisibility">
			<EyeIcon v-if="showPassword" />
			<EyeOffIcon v-else />
		</div>
		<div v-if="type !== 'textarea'" class="CustomInput-BottomLine"></div>
		<div v-show="helperText" class="helper">
			<div ref="helperTextContent"
				 class="helperTextContent littleAndBold">{{ helperText }}
			</div>
		</div>
		<div v-if="validationError" class="validationError">
			{{ validationError }}
		</div>
	</div>
</template>

<script>
import { mapActions } from 'vuex'
import lang from '../lang'
import { signUpValidate } from '../util/ajax'
import EyeIcon from 'vue-material-design-icons/Eye.vue'
import EyeOffIcon from 'vue-material-design-icons/EyeOff.vue'

function debounce(callback, time) {
	let interval
	return function(...args) {
		clearTimeout(interval)
		interval = setTimeout(() => {
			interval = null
			callback.apply(this, args)
		}, time)
	}
}

export default {
	name: 'Input',
	props: {
		label: String,
		type: {
			type: String,
			default: 'text',
		},
		dataKey: String,
		placeholder: String,
		helperText: String,
		skipWritingToState: Boolean,
		validationKey: String,
		validationFunction: Function,
		optional: {
			validator() {
				return true
			}, // Any value goes
		},
	},
	data() {
		return {
			showPassword: false,
			value: this.$store.state[this.dataKey],
			hasFocus: false,
			validationError: null,
			lang,
		}
	},
	mounted() {
		if (this.type === 'password') {
			if (this.$store.state.showPassword) {
				this.showPassword = true
			}
			window.generatePassword = event => {
				event.preventDefault()

				let password = ''
				const chars = 'abcdefghiklmnopqrstuvwxyz0123456789'
				function addLetter() {
					password += chars[(chars.length * Math.random()) | 0]
				}
				for (let i = 0; i < 8; i++) addLetter()

				this.value = password
				this.showPassword = true
				this.setPartnerDataValue({ key: 'showPassword', value: this.showPassword })
			}
			this.$refs.helperTextContent.innerHTML = `${this.helperText.replace(
				'{0}',
				'<a class="greenLink" onclick="generatePassword(event)" href="#">' +
					lang.signup.account.password.helperTextLinkText +
					'<a>'
			)}`
		}

		this.debouncedValidate = debounce(async function(value) {
			if (this.validationFunction) {
				let errorMessage = this.validationFunction(value)
				this.validationError = errorMessage || null
				return
			}

			if (!this.validationKey || !value) {
				this.validationError = null
				return
			}

			let validationResults = await signUpValidate({
				[this.validationKey]: value,
				countryCode: this.$store.state.country,
			})

			if (validationResults[this.validationKey].valid) {
				this.validationError = null
			} else {
				this.validationError = validationResults[this.validationKey].message
			}
		}, 500)

		this.debouncedValidate(this.value)
	},
	computed: {
		overriddenType() {
			if (this.type === 'password' && this.showPassword) {
				return 'text'
			}
		},
	},
	watch: {
		value(value) {
			if (!this.skipWritingToState) this.setPartnerDataValue({ key: this.dataKey, value })

			this.$emit('lazyValueChanged', value)

			this.debouncedValidate(value)
		},
	},
	methods: {
		...mapActions(['setPartnerDataValue']),
		oninput() {
			this.$emit('input', this.$refs.inputElement.value)

			this.debouncedValidate(this.$refs.inputElement.value)
		},
		onchange() {
			this.$emit('manualChangeEvent', this.$refs.inputElement.value)
		},

		// blur is used from outside of this file
		blur() {
			this.$refs.inputElement.blur()
		},

		onblur() {
			this.hasFocus = false
			this.$emit('blur')
		},

		debouncedValidate: () => {}, // will be generated on mount

		togglePasswordVisibility() {
			this.showPassword = !this.showPassword
			this.setPartnerDataValue({ key: 'showPassword', value: this.showPassword })
		},
	},
	components: {
		EyeIcon,
		EyeOffIcon,
	},
}
</script>

<style lang="stylus" scoped>
.CustomInput {
	position: relative;
	margin: 20px 0;
	width: 100%;
}

.CustomInput > label {
	display: block;
	font-size: 13px;
	color: #888;
	transition: color 0.15s;
	position: relative;
	top: 0px;
	transition: all 0.2s;
	height: 16px;
}

.CustomInput:not(.has-focus):not(.has-value):not(.has-placeholder):not(.is-textarea) > label {
	top: 25px;
	font-size: 16px;
	user-select: none;
	pointer-events: none;
}

.CustomInput > input, .CustomInput > textarea {
	display: block;
	border: none;
	width: 100%;
	height: 32px;
	font-size: 16px;
	outline: none;
	margin: 0;
	padding: 0;
	caret-color: #1bcbac;
}

.CustomInput::after {
	box-sizing: border-box;
	content: ' ';
	display: block;
	width: 100%;
	height: 2px;
	border-bottom: 1px solid #888;
	position: relative;
	top: -3px;
	transition: border-color 0.2s;
}

.CustomInput:hover::after {
	border-bottom: 1px solid black;
}

.CustomInput.has-focus::after {
	border-bottom: 1px solid #1bcbac;
}

.CustomInput-BottomLine {
	background: #1bcbac;
	height: 2px;
	position: absolute;
	bottom: 3px;
	width: 100%;
	margin: 0 auto;
	display: inline-block;
	transition: transform 0.2s;
	left: 50%;
	transform: translateX(-50%) scale(0);
}

.CustomInput.has-focus .CustomInput-BottomLine {
	transform: translateX(-50%) scale(1);
}

.CustomInput.has-focus > label {
	color: #1bcbac;
}

.CustomInput.has-validationError {
	margin-bottom: calc(20px + 16px);
}

.CustomInput.has-validationError > label {
	color: red;
}

@media screen and (max-width: 1300px) {
	.helper {
		display: none;
	}
}

.helper {
	position: absolute;
	top: 0px;
	right: -340px;
	width: 300px;
	height: 60px;
	background: #eee;
	z-index: 100;
	border-top-right-radius: 6px;
	border-bottom-right-radius: 6px;
	padding: 5px;
	font-size: 13px;
	line-height: 1.15rem;
	box-sizing: border-box;
	opacity: 1;
	transition: all 0.3s;

	.helperTextContent {
		width: calc(100% - 20px);
		position: absolute;
		left: 10px;
		top: 50%;
		transform: translateY(-50%);
		z-index: 1000;
	}
}

.helper::before {
	content: '';
	position: absolute;
	top: 0;
	background: #eee;
	z-index: -4;
	left: -30px;
	height: 60px;
	width: 60px;
	transform: rotate(45deg) scale(0.707106781187);
}

.CustomInput:not(.has-focus) .helper {
	opacity: 0;
}

.validationError {
	color: red;
	position: absolute;
	bottom: -16px;
	font-size: 12px;
}

.viewPasswordButton {
    position: absolute;
    right: 0;
    bottom: -5px;
    width: 50px;
    height: 50px;
    text-align: center;
    cursor: pointer;
    border-radius: 50%;

	opacity: 0.6;

	font-size: 20px;

	> * {
		position: absolute;
		top: 47%; /* not 50% because in chrome 47% looks better */
		transform: translateY(-50%) translateX(-50%);
	}

	&:hover {
		background: #eee;
	}
	&:active {
		background: #ddd;
	}
}

.CustomInput > textarea {
	resize: vertical;
	min-height: 60px;
	max-height: 200px;
	border: 1px solid #888;
}
.CustomInput.has-focus > textarea {
	border-color: #1bcbac;
}
.CustomInput.is-textarea::after {
	border: none
}
.CustomInput.is-textarea > label {
	top: -7px
}

</style>
